import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, ArrowUpRight } from "lucide-react";
import { CardGridSkeleton } from "@/components/skeletons/PageSkeletons";

function pctColor(pct) {
  if (pct >= 90) return "text-[#059669]";
  if (pct >= 75) return "text-[#C8102E]";
  if (pct >= 60) return "text-[#D97706]";
  return "text-[#E11D48]";
}
function pctBar(pct) {
  if (pct >= 90) return "bg-[#059669]";
  if (pct >= 75) return "bg-[#C8102E]";
  if (pct >= 60) return "bg-[#D97706]";
  return "bg-[#E11D48]";
}

export default function Sections() {
  const [sections, setSections] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/sections").then(r => setSections(r.data)),
      api.get("/dashboard/stats").then(r => setDashboard(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const pctMap = new Map((dashboard?.section_stats || []).map(s => [s.id, s.overall_pct]));

  return (
    <div className="min-h-screen">
      <div className="px-6 md:px-12 pt-10 pb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">Student groups</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0F172A] mt-2" data-testid="sections-heading">Sections.</h1>
        <p className="text-[#475569] mt-3 max-w-2xl">Every student belongs to exactly one section. Sections carry their own timetable and roster.</p>
      </div>

      <div className="px-6 md:px-12 pb-16">
        {loading ? (
          <CardGridSkeleton testId="sections-loading" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sections-grid">
            {sections.map(s => {
              const pct = pctMap.get(s.id) ?? 0;
              return (
                <button
                  key={s.id}
                  onClick={() => navigate(`/sections/${s.id}`)}
                  className="text-left bg-white border border-[#E2E8F0] rounded-lg p-6 hover:border-[#C8102E] transition-colors duration-200 ease-in-out"
                  data-testid={`section-card-${s.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#FDECEF] text-[#C8102E] flex items-center justify-center">
                      <Users className="w-5 h-5" />
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-[#94A3B8]" />
                  </div>
                  <div className="font-heading text-2xl font-bold tracking-tight text-[#0F172A]">Section {s.name}</div>
                  <div className="text-sm text-[#475569] mt-1">{s.student_count} students · {s.class_count} classes</div>
                  <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-[#94A3B8] font-bold uppercase tracking-[0.15em]">Overall</span>
                      <span className={`font-heading font-bold ${pctColor(pct)}`}>{pct}%</span>
                    </div>
                    <div className="h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                      <div className={`h-full ${pctBar(pct)}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
